import type { ModelPricingEntry } from "@/types/aicheck";

export const FALLBACK_MODEL_PRICING_TABLE: ModelPricingEntry[] = [
  {
    model: "gpt-5.4-pro",
    input: 30.0,
    output: 180.0,
    contextWindow: 1050000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5.4",
    input: 2.5,
    output: 15.0,
    contextWindow: 1050000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5.2",
    input: 1.75,
    output: 14.0,
    contextWindow: 400000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5.1",
    input: 1.25,
    output: 10.0,
    contextWindow: 400000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5",
    input: 1.25,
    output: 10.0,
    contextWindow: 400000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5-mini",
    input: 0.25,
    output: 2.0,
    contextWindow: 400000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-5-nano",
    input: 0.05,
    output: 0.4,
    contextWindow: 400000,
    maxOutputTokens: 128000,
  },
  {
    model: "gpt-4.1",
    input: 2.0,
    output: 8.0,
    contextWindow: 1047576,
    maxOutputTokens: 32768,
  },
  {
    model: "gpt-4.1-mini",
    input: 0.4,
    output: 1.6,
    contextWindow: 1047576,
    maxOutputTokens: 32768,
  },
  {
    model: "gpt-4.1-nano",
    input: 0.1,
    output: 0.4,
    contextWindow: 1047576,
    maxOutputTokens: 32768,
  },
  {
    model: "gpt-4o",
    input: 2.5,
    output: 10.0,
    contextWindow: 128000,
    maxOutputTokens: 16384,
  },
  {
    model: "gpt-4o-mini",
    input: 0.15,
    output: 0.6,
    contextWindow: 128000,
    maxOutputTokens: 16384,
  },
  // Embeddings（出力トークンなし）。未登録だと DEFAULT_MODEL_PRICING の $2.5/1M へ
  // フォールバックし、実際の 125 倍で計上される。必ず表に持たせること。
  {
    model: "text-embedding-3-small",
    input: 0.02,
    output: 0,
    contextWindow: 8191,
    maxOutputTokens: 0,
  },
  {
    model: "text-embedding-3-large",
    input: 0.13,
    output: 0,
    contextWindow: 8191,
    maxOutputTokens: 0,
  },
  // ── Anthropic ────────────────────────────────────────────────────────────
  // cacheWrite は input の 1.25 倍（5 分 TTL）。OpenAI と違い**書き込みが割増**
  // なので、省略して input 扱いにすると過小見積もりになる。
  // cacheRead は input の 0.1 倍。
  {
    model: "claude-sonnet-5",
    provider: "anthropic",
    input: 3.0,
    output: 15.0,
    cacheWrite: 3.75,
    cacheRead: 0.3,
    contextWindow: 1000000,
    maxOutputTokens: 128000,
  },
  {
    model: "claude-opus-5",
    provider: "anthropic",
    input: 5.0,
    output: 25.0,
    cacheWrite: 6.25,
    cacheRead: 0.5,
    contextWindow: 1000000,
    maxOutputTokens: 128000,
  },
  {
    model: "claude-haiku-4-5",
    provider: "anthropic",
    input: 1.0,
    output: 5.0,
    cacheWrite: 1.25,
    cacheRead: 0.1,
    contextWindow: 200000,
    maxOutputTokens: 64000,
  },
];

const DEFAULT_MODEL_PRICING: ModelPricingEntry = {
  model: "default",
  input: 2.5,
  output: 10.0,
  contextWindow: 128000,
  maxOutputTokens: 16384,
};

export function estimateTokenCost(
  model: string,
  tokens: { prompt: number; completion: number },
  pricingTable: ModelPricingEntry[] = FALLBACK_MODEL_PRICING_TABLE,
  provider?: string,
): number {
  const pricing = findModelPricingEntry(model, pricingTable, provider) ?? {
    ...DEFAULT_MODEL_PRICING,
    model,
  };
  return (
    (tokens.prompt / 1_000_000) * pricing.input +
    (tokens.completion / 1_000_000) * pricing.output
  );
}

/** キャッシュ読み出し単価。未設定なら旧 cachedInput、それも無ければ input の 10%。 */
export function cacheReadRate(pricing: ModelPricingEntry): number {
  return pricing.cacheRead ?? pricing.cachedInput ?? pricing.input * 0.1;
}

/**
 * キャッシュ書き込み単価。未設定なら input（= 割増なし）。
 * OpenAI は書き込みに追加課金がないので既定で正しいが、Anthropic は割増なので
 * 価格表に cacheWrite を明示しないと過小見積もりになる。
 */
export function cacheWriteRate(pricing: ModelPricingEntry): number {
  return pricing.cacheWrite ?? pricing.input;
}

/**
 * キャッシュを考慮したコスト見積もり。
 *
 * `cacheWrite` / `cacheRead` を分けているのは、OpenAI（読み出し割引のみ）と
 * Anthropic（**書き込みが 1.25 倍の割増**・読み出しが 0.1 倍）で経済が逆向きだから。
 * 旧来の `cached`（読み出し相当）だけを渡す呼び出しも従来どおり動く。
 */
export function estimateDetailedTokenCost(
  model: string,
  tokens: {
    prompt: number;
    /** @deprecated cacheRead を使うこと。読み出しトークンとして解釈される。 */
    cached?: number;
    cacheRead?: number;
    cacheWrite?: number;
    completion: number;
  },
  pricingTable: ModelPricingEntry[] = FALLBACK_MODEL_PRICING_TABLE,
  provider?: string,
): number {
  const pricing = findModelPricingEntry(model, pricingTable, provider) ?? {
    ...DEFAULT_MODEL_PRICING,
    model,
  };
  // prompt_tokens は「キャッシュ読み書きを含む入力総数」。内訳を引いた残りが定価。
  const read = Math.min(tokens.cacheRead ?? tokens.cached ?? 0, tokens.prompt);
  const write = Math.min(tokens.cacheWrite ?? 0, tokens.prompt - read);
  const freshPrompt = tokens.prompt - read - write;
  return (
    (freshPrompt / 1_000_000) * pricing.input +
    (read / 1_000_000) * cacheReadRate(pricing) +
    (write / 1_000_000) * cacheWriteRate(pricing) +
    (tokens.completion / 1_000_000) * pricing.output
  );
}

/**
 * 価格表からモデルを引く。
 *
 * provider を指定すると (1) その provider と明示されたエントリ →
 * (2) provider 未指定の「未分類」エントリ の順に探す。
 * **他プロバイダと明示されたエントリには決して落とさない** —— 落とすと
 * anthropic のモデルを openai の単価で計上しうるうえ、価格表の登録漏れを
 * validateModelIds が検出できなくなる（fail-closed が崩れる）。
 * 未分類を次点に許すのは、既存 settings.yaml のエントリが provider を持たず、
 * 手編集で claude を足すときも付け忘れが起きるため（移行期の実用上の妥協）。
 * provider を省略した場合は従来どおりモデル名だけで引く。
 */
export function findModelPricingEntry(
  model: string,
  table: ModelPricingEntry[],
  provider?: string,
): ModelPricingEntry | undefined {
  if (!provider) {
    return (
      table.find((entry) => entry.model === model) ??
      longestPrefixMatch(model, table)
    );
  }

  const owned = table.filter((entry) => entry.provider === provider);
  // provider 未指定のエントリ（既存 settings.yaml の大半）は「未分類」として
  // 次点で採用する。**他プロバイダと明示されたエントリには絶対に落とさない**
  // ——落とすと anthropic のモデルを openai の単価で計上しうるうえ、
  // 価格表の登録漏れを validateModelIds が検出できなくなる。
  const unclassified = table.filter((entry) => entry.provider === undefined);

  return (
    owned.find((entry) => entry.model === model) ??
    longestPrefixMatch(model, owned) ??
    unclassified.find((entry) => entry.model === model) ??
    longestPrefixMatch(model, unclassified)
  );
}

/**
 * 最長前方一致。**表の並び順に依存しない**。
 *
 * API は日付サフィックス付きの解決済み名を返す（例 "gpt-5-mini-2025-08-07"）。
 * 単純に先頭から find すると、表に "gpt-5" が "gpt-5-mini" より先にある場合に
 * "gpt-5"（$1.25/$10）へ一致し、正しい "gpt-5-mini"（$0.25/$2）の 5 倍で
 * 計上される。より長い（＝より具体的な）エントリを常に優先する。
 */
function longestPrefixMatch(
  model: string,
  table: ModelPricingEntry[],
): ModelPricingEntry | undefined {
  let best: ModelPricingEntry | undefined;
  for (const entry of table) {
    if (!model.startsWith(entry.model)) continue;
    if (!best || entry.model.length > best.model.length) best = entry;
  }
  return best;
}
